import { getFlags } from "@/lib/flags";
import { functions } from "./firebase";
import { httpsCallable } from "firebase/functions";
import { accountStorage } from "./accountStorage";
import { logLibraryEvent } from "./libraryAnalytics";

const failMode = () => new URLSearchParams(window.location.search).get("fail");

export async function getSourceCatalog(params = {}) {
  const cache = accountStorage();
  if (failMode() === "read") return { error: "read" };
  try {
    const fn = httpsCallable(functions, "getSourceCatalog");
    const payload = {};
    if (typeof params.q === "string" && params.q.trim().length > 0) {
      payload.q = params.q.trim();
    }
    if (typeof params.subject === "string" && params.subject.trim().length > 0) {
      payload.subject = params.subject.trim();
    }
    if (typeof params.language === "string" && params.language.trim().length > 0) {
      payload.language = params.language.trim().toLowerCase();
    }
    if (
      params.availability === "all" ||
      params.availability === "hosted" ||
      params.availability === "link_only"
    ) {
      payload.availability = params.availability;
    }
    if (
      params.rightsClass === "HOST" ||
      params.rightsClass === "HOST_ATTRIBUTION" ||
      params.rightsClass === "LINK_ONLY"
    ) {
      payload.rightsClass = params.rightsClass;
    }
    if (typeof params.cursor === "string" && params.cursor.trim().length > 0) {
      payload.cursor = params.cursor.trim();
    }
    if (typeof params.limit === "number" && Number.isInteger(params.limit)) {
      payload.limit = params.limit;
    }

    const res = await fn(payload);
    if (!cache.isCurrent()) return { error: "read" };
    if (res.data?.ok) {
      void logLibraryEvent("library_search", {
        availability,
        resultCount: Array.isArray(res.data.sources) ? res.data.sources.length : 0,
        hasQuery: queryText.length > 0,
        hasSubject: subject !== null,
      });
      return {
        sources: Array.isArray(res.data.sources) ? res.data.sources : [],
        nextCursor: res.data.nextCursor ?? null,
        locked: Boolean(res.data.locked || getFlags().locked),
        catalogEnabled: res.data.catalogEnabled !== false,
      };
    }
    return { error: "read" };
  } catch (err) {
    if (err?.code === "functions/permission-denied") {
      return {
        sources: [],
        nextCursor: null,
        locked: true,
        catalogEnabled: true,
      };
    }
    return { error: "read" };
  }
}

export async function getSource(sourceId) {
  const cache = accountStorage();
  if (failMode() === "read") return { error: "read" };
  try {
    const fn = httpsCallable(functions, "getSource");
    const res = await fn({ sourceId });
    if (!cache.isCurrent()) return { error: "read" };
    if (res.data?.ok && res.data.source) {
      return {
        source: res.data.source,
        locked: Boolean(getFlags().locked),
      };
    }
    return { error: "missing" };
  } catch (err) {
    if (err?.code === "functions/not-found") return { error: "missing" };
    if (err?.code === "functions/permission-denied") return { error: "locked" };
    return { error: "read" };
  }
}

export function formatBookAttributionForUi(book) {
  if (!book || typeof book !== "object") {
    return { showAttribution: false };
  }
  const origin = book.origin === "open" ? "open" : "original";
  const rightsClass = book.rightsClass || (origin === "original" ? "HOST" : "PENDING_REVIEW");
  const rawAttr = book.attribution;

  if (origin !== "open" && !rawAttr) {
    return { showAttribution: false };
  }

  if (typeof rawAttr === "string") {
    const trimmed = rawAttr.trim();
    return {
      showAttribution: origin === "open" || trimmed.length > 0,
      origin,
      rightsClass,
      sourceId: book.sourceId || null,
      text: trimmed || null,
      title: null,
      authors: [],
      licenseCode: null,
      licenseUrl: null,
      sourceUrl: null,
      summarizedByAxoris: rightsClass === "HOST_ATTRIBUTION",
    };
  }

  const attrObj =
    rawAttr && typeof rawAttr === "object" && !Array.isArray(rawAttr)
      ? rawAttr
      : {};

  const authors = Array.isArray(attrObj.authors)
    ? attrObj.authors.filter((a) => typeof a === "string" && a.trim().length > 0)
    : typeof attrObj.author === "string" && attrObj.author.trim().length > 0
      ? [attrObj.author.trim()]
      : [];

  return {
    showAttribution: true,
    origin,
    rightsClass,
    sourceId: book.sourceId || null,
    text: typeof attrObj.text === "string" ? attrObj.text.trim() : null,
    title: typeof attrObj.title === "string" ? attrObj.title.trim() : null,
    authors,
    licenseCode:
      typeof attrObj.licenseCode === "string" ? attrObj.licenseCode.trim() : null,
    licenseUrl:
      typeof attrObj.licenseUrl === "string" &&
      attrObj.licenseUrl.trim().startsWith("https://")
        ? attrObj.licenseUrl.trim()
        : null,
    sourceUrl:
      typeof attrObj.sourceUrl === "string" &&
      attrObj.sourceUrl.trim().startsWith("https://")
        ? attrObj.sourceUrl.trim()
        : null,
    summarizedByAxoris:
      attrObj.summarizedByAxoris === true || rightsClass === "HOST_ATTRIBUTION",
  };
}
